import { Component, defineElement, FC, useBindMethod, useElement, useProp } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { Button, ButtonInterface } from '@source/components/atoms/button/button';

type ButtonToggleType = 'toggle' | 'switch';
type ButtonToggleDirection = 'row' | 'column';

const TOGGLE_ON_CLASS = 'button-toggle--on';
const TOGGLE_OFF_CLASS = 'button-toggle--off';

export interface ButtonToggleElement extends Component {
    isToggleOn: boolean;
    setToggleOn: (toggleOn: boolean) => void;
}

const ButtonToggle: FC<ButtonToggleElement> = () => {
    const [type] = useProp<ButtonToggleType>('type', 'toggle');
    const [direction] = useProp<ButtonToggleDirection>('direction', 'row');
    const [toggleOn] = useProp<ButtonInterface>('toggleOn');
    const [toggleOff] = useProp<ButtonInterface>('toggleOff');
    const [classes] = useProp<string>('classes', '');
    const [onClick] = useProp<Function | null>('onClick', null);
    const [isToggleOn, setToggleOn, watchToggleOn] = useProp<boolean>('isToggleOn', false);

    const element = useElement<HTMLDivElement>('[js-hook-button-toggle]');

    useBindMethod('setToggleOn', setToggleOn);

    watchToggleOn((on) => {
        if (on) {
            element.current!.classList.add(TOGGLE_ON_CLASS);
            element.current!.classList.remove(TOGGLE_OFF_CLASS);
        } else {
            element.current!.classList.remove(TOGGLE_ON_CLASS);
            element.current!.classList.add(TOGGLE_OFF_CLASS);
        }
    });

    const createHandleClick = (options: { toggleClick?: Function; toggleOn: boolean }) => {
        return (e: Event) => {
            setToggleOn(options.toggleOn);
            onClick && onClick(e, options.toggleOn);
            options.toggleClick && options.toggleClick(e, toggleOn);
        };
    };

    const wrapperClass = {
        ['c-button-toggle']: true,
        [`button-toggle--${type}`]: true,
        [`button-toggle--${direction}`]: true,
        [classes]: true,
        [isToggleOn ? TOGGLE_ON_CLASS : TOGGLE_OFF_CLASS]: true,
    };

    return (
        <div class={wrapperClass} js-hook-button-toggle>
            <div class="button-toggle__button-off" js-hook-button-toggle-off>
                <Button
                    {...toggleOff}
                    onClick={createHandleClick({ toggleOn: true, toggleClick: toggleOff.onClick })}
                />
            </div>
            <div class="button-toggle__button-on" js-hook-button-toggle-on>
                <Button
                    {...toggleOn}
                    onClick={createHandleClick({ toggleOn: false, toggleClick: toggleOn.onClick })}
                />
            </div>
        </div>
    );
};

ButtonToggle.props = {
    type: {
        type: String,
        reflectToAttr: true,
    },
    direction: {
        type: String,
        reflectToAttr: true,
    },
    toggleOn: {
        type: Object,
        required: true,
    },
    toggleOff: {
        type: Object,
        required: true,
    },
    classes: {
        type: String,
        reflectToAttr: true,
    },
    onClick: {
        type: Function,
    },
    isToggleOn: {
        type: Boolean,
        reflectToAttr: true,
    },
};

defineElement('button-toggle', ButtonToggle);
